import { useState } from 'react';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { useValidityStatusConfig } from 'components/structure-grids/validity-status-indicator';
import request from 'lib/request';
import type {
  TypeCurveValidationResponse,
  TypeCurveValidationResult,
} from 'types/api-responses';
import styles from './type-curve-validation-status.module.scss';

interface TypeCurveValidationCellProps {
  id: number;
  validity?: string | null;
  className?: string;
  iconOverride?: React.ReactElement | null;
}

const normalizeValidity = (validity?: string | null): string =>
  String(validity ?? '').toLowerCase();

/**
 * Renders the "Validation" column cell for the Type Curves listing.
 *
 * Shows the rollup `validity` icon + label (reusing the structure-grids icon
 * mapping). On hover it lazily fetches the per–type-curve validation details
 * from `/queries/type_curve_header_details/validations` and lists each check's label,
 * status, and failure messages inside a floating tooltip.
 *
 * @param {TypeCurveValidationCellProps} props - Component props
 * @return {JSX.Element} The validation status cell
 */
export const TypeCurveValidationCell = ({
  id,
  validity,
  className,
  iconOverride,
}: TypeCurveValidationCellProps) => {
  const normalized = normalizeValidity(validity);
  const { icon: hookIcon, tooltip } = useValidityStatusConfig(
    normalized || null
  );
  const icon = iconOverride ?? hookIcon;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TypeCurveValidationResult[] | null>(
    null
  );
  const [fetched, setFetched] = useState(false);

  // No decided validity -> match the Verification column's "--" placeholder.
  if (!icon) {
    return <>--</>;
  }

  const fetchValidations = async () => {
    if (fetched) return;
    setFetched(true);
    setLoading(true);
    setError(null);

    const response = await request.post<TypeCurveValidationResponse, unknown>(
      '/queries/type_curve_header_details/validations',
      { type_curve_header_details: [{ id }] },
      new AbortController()
    );

    setLoading(false);
    if (response.type === 'success') {
      setResults(response.data?.results ?? []);
    } else {
      setError('Failed to load validation details');
    }
  };

  const renderTooltipBody = () => {
    if (loading) {
      return <span>Loading validation details…</span>;
    }
    if (error) {
      return <span className={styles.error}>{error}</span>;
    }
    if (results && results.length > 0) {
      return results.map((result, index) => {
        const meta = result.displayMetadata;
        return (
          <div key={index} className={styles.validationItem}>
            <div className={styles.validationHeader}>
              <span className={styles.label}>{meta?.label || '--'}</span>
              <span
                className={
                  result.isValid ? styles.passedStatus : styles.failedStatus
                }
              >
                {meta?.status || '--'}
              </span>
            </div>
            {meta?.failureMessages?.length ? (
              <ul className={styles.failureMessages}>
                {meta.failureMessages.map((message, messageIndex) => (
                  <li key={messageIndex}>{message}</li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      });
    }
    return <span>No validation data available</span>;
  };

  return (
    <FloatingTooltip
      content={
        <div className={styles.tooltipContent}>{renderTooltipBody()}</div>
      }
      placement="top"
    >
      <span
        className={`${styles.validationCell}${
          className ? ` ${className}` : ''
        }`}
        onMouseEnter={fetchValidations}
        onFocus={fetchValidations}
      >
        {icon}
        <span>{tooltip}</span>
      </span>
    </FloatingTooltip>
  );
};

export default TypeCurveValidationCell;
